import React, { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { Button } from "@/components/ui/Button";
import { toast } from "sonner";
import { Loader2, X, Inbox, FileText, ChevronRight } from "lucide-react";

const STATUS_META = {
  pending:     { label: "În așteptare", fg: "#a16207", bg: "#fef9c3" },
  in_progress: { label: "În lucru",     fg: "#1d4ed8", bg: "#dbeafe" },
  resolved:    { label: "Rezolvată",    fg: "#166534", bg: "#dcfce7" },
  rejected:    { label: "Respinsă",     fg: "#b91c1c", bg: "#fee2e2" },
};
const STATUS_OPTIONS = ["pending", "in_progress", "resolved", "rejected"];

function StatusPill({ status }) {
  const m = STATUS_META[status] || { label: status, fg: "#374151", bg: "#f4f4f5" };
  return (
    <span className="inline-flex items-center px-2.5 h-6 text-[11px] font-semibold rounded-full"
      style={{ background: m.bg, color: m.fg }}>{m.label}</span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}

export default function RequestsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/staff/requests");
      setItems(data || []);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() =>
    filter === "all" ? items : items.filter((r) => r.status === filter),
  [items, filter]);

  const counts = useMemo(() => ({
    all: items.length,
    ...STATUS_OPTIONS.reduce((acc, s) => ({ ...acc, [s]: items.filter((r) => r.status === s).length }), {}),
  }), [items]);

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1100px] mx-auto">
        <header className="pb-8 pt-2">
          <div className="text-[12px] font-display font-medium text-ink-muted mb-3">HR / administrativ</div>
          <h1 className="text-[38px] md:text-[46px] leading-[1.05] font-display font-semibold tracking-[-0.02em] text-ink">
            Cereri angajați
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-ink-muted max-w-xl">
            Cererile de documente trimise de angajați. Actualizează statusul și adaugă un răspuns.
          </p>
        </header>

        <div className="pio-tabs mb-6">
          {[{ k: "all", l: "Toate" }, ...STATUS_OPTIONS.map((s) => ({ k: s, l: STATUS_META[s].label }))].map((t) => (
            <button key={t.k} onClick={() => setFilter(t.k)}
              data-testid={`requests-filter-${t.k}`}
              className={`pio-tab !text-[14px] ${filter === t.k ? "is-active" : ""}`}>
              {t.l}
              <span className="text-[11px] font-mono tabular-nums opacity-60 ml-1.5">({counts[t.k] || 0})</span>
            </button>
          ))}
        </div>

        <div className="surface overflow-hidden" data-testid="admin-requests-list">
          {loading ? (
            <div className="py-20 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-ink-muted" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center text-ink-muted">
              <Inbox className="h-8 w-8 mx-auto text-neutral-300 mb-3" />
              <div className="text-[15px]">Nu există cereri în această categorie.</div>
            </div>
          ) : (
            <div className="divide-y divide-neutral-100">
              {filtered.map((r) => (
                <button key={r.id} onClick={() => setDetail(r)}
                  data-testid={`admin-request-row-${r.id}`}
                  className="group w-full flex items-center gap-4 px-5 py-4 hover:bg-neutral-50 transition-colors text-left">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center bg-neutral-100 text-ink-muted shrink-0">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2.5 mb-1 flex-wrap">
                      <div className="text-[15px] font-medium text-ink truncate">{r.document_type}</div>
                      <StatusPill status={r.status} />
                    </div>
                    <div className="text-[13px] text-ink-muted truncate">
                      {r.employee_name} · {fmtDate(r.created_at)}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-neutral-300 group-hover:text-ink group-hover:translate-x-0.5 transition-all" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <RequestDetail request={detail} onClose={() => setDetail(null)}
        onSaved={async () => { setDetail(null); await load(); }} />
    </PortalLayout>
  );
}

function RequestDetail({ request, onClose, onSaved }) {
  const [status, setStatus] = useState("pending");
  const [response, setResponse] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (request) { setStatus(request.status); setResponse(request.hr_response || ""); }
  }, [request]);

  useEffect(() => {
    document.body.style.overflow = request ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [request]);

  if (!request) return null;

  async function save() {
    setBusy(true);
    try {
      await api.put(`/staff/requests/${request.id}`, { status, hr_response: response });
      toast.success("Cerere actualizată");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-ink/20 z-40" />
      <aside className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-[560px] bg-white border-l border-neutral-200 flex flex-col">
        <div className="px-8 pt-8 pb-6 border-b border-neutral-100 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[12px] font-display font-medium text-ink-muted mb-2 flex items-center gap-3">
              Cerere document <StatusPill status={request.status} />
            </div>
            <div className="text-[22px] font-display font-semibold text-ink">{request.document_type}</div>
            <div className="text-[13px] text-ink-muted mt-1">
              {request.employee_name} · {fmtDate(request.created_at)}
            </div>
          </div>
          <button onClick={onClose}
            className="h-10 w-10 flex items-center justify-center text-ink-muted hover:text-ink hover:bg-neutral-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-8 space-y-6">
          {request.details && (
            <section>
              <div className="text-[12px] font-display font-medium text-ink-muted mb-2">Detalii angajat</div>
              <div className="text-[14px] text-ink">{request.details}</div>
            </section>
          )}
          <section>
            <div className="text-[12px] font-display font-medium text-ink-muted mb-2">Status</div>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
              data-testid="admin-request-status"
              className="w-full border border-neutral-200 rounded-lg px-3 py-2.5 text-[14px] outline-none focus:border-ink bg-white">
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
            </select>
          </section>
          <section>
            <div className="text-[12px] font-display font-medium text-ink-muted mb-2">Răspuns pentru angajat</div>
            <textarea rows={4} value={response} onChange={(e) => setResponse(e.target.value)}
              data-testid="admin-request-response"
              placeholder="Ex: Documentul este pregătit și poate fi ridicat de la birou HR."
              className="w-full border border-neutral-200 rounded-lg p-3 text-[14px] outline-none focus:border-ink resize-none" />
          </section>
        </div>

        <div className="px-8 py-5 border-t border-neutral-100 flex items-center justify-end gap-3">
          <Button variant="ghost" size="md" onClick={onClose}>Închide</Button>
          <Button variant="primary" size="md" loading={busy} onClick={save} data-testid="admin-request-save">
            Salvează
          </Button>
        </div>
      </aside>
    </>
  );
}
